import React, { useMemo, useState, useEffect } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { getUserRelatives } from '../../../../services/bookingService'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

const PAGE_SIZE = 10

export type RelativeItem = {
    id: number
    first_name?: string
    last_name?: string
    full_name?: string
    fullName?: string
    year_of_birth?: number
    yearOfBirth?: number
    age?: number
    category?: 'adult' | 'youth' | 'child' | 'infant'
    relationship?: string
}

type Props = {
    values: number[]
    onChange: (val: number[]) => void
    experience?: any
    placeholder?: string
    minChars?: number
    maxItems?: number
    onRelativesLoaded?: (relatives: RelativeItem[]) => void
}

export function GuestsAutocompleteField({
    values,
    onChange,
    experience,
    placeholder = 'Select from your relatives',
    minChars = 0,
    maxItems,
    onRelativesLoaded,
}: Props) {

    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [relatives, setRelatives] = useState<RelativeItem[]>([])
    const [loading, setLoading] = useState(false)
    const [debounced, setDebounced] = useState('')
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

    useEffect(() => {
        const id = setTimeout(() => setDebounced(query.trim()), 200)
        return () => clearTimeout(id)
    }, [query])

    const fetchData = async () => {
        try {
            setLoading(true)
            const list = await getUserRelatives()
            const rawList = Array.isArray(list) ? list : (list?.results || [])
            const currentYear = new Date().getFullYear()
            const parsedList: RelativeItem[] = rawList.map((r: any) => {
                const year = r.year_of_birth ?? r.yearOfBirth
                let computedAge = r.age
                let computedCat = r.category
                if (year && !computedAge) {
                    computedAge = currentYear - year
                }
                if (computedAge !== undefined) {
                    if (computedAge >= 18) computedCat = 'adult'
                    else if (computedAge >= 13) computedCat = 'youth'
                    else if (computedAge >= 2) computedCat = 'child'
                    else computedCat = 'infant'
                }
                const name = r.full_name || r.fullName || `${r.first_name || r.firstName || ''} ${r.last_name || r.lastName || ''}`.trim()
                return {
                    id: r.id,
                    first_name: r.first_name || r.firstName,
                    last_name: r.last_name || r.lastName,
                    fullName: name,
                    full_name: name,
                    year_of_birth: year,
                    yearOfBirth: year,
                    age: computedAge,
                    category: computedCat,
                    relationship: typeof r.relationship === 'object' ? r.relationship?.name : r.relationship,
                }
            })

            setRelatives(parsedList)
            if (onRelativesLoaded) {
                onRelativesLoaded(parsedList)
            }
        } catch (err) {
            console.error('Error fetching user relatives', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const filteredRelatives = useMemo(() => {
        // Excluir parientes que ya hayan sido seleccionados
        const unselected = relatives.filter((r) => !values.includes(r.id))
        if (!debounced) return unselected
        const q = debounced.toLowerCase()
        return unselected.filter((r) =>
            (r.fullName ?? '').toLowerCase().includes(q) ||
            (r.relationship ?? '').toLowerCase().includes(q) ||
            (r.category ?? '').toLowerCase().includes(q)
        )
    }, [relatives, debounced, values])

    useEffect(() => {
        setVisibleCount(PAGE_SIZE)
    }, [debounced])

    const visible = useMemo(
        () => filteredRelatives.slice(0, visibleCount),
        [filteredRelatives, visibleCount]
    )

    const hasMore = filteredRelatives.length > visibleCount

    const handleScroll = useNearBottom(() => {
        if (hasMore) setVisibleCount((c) => c + PAGE_SIZE)
    })

    const highlightMatch = (text: string, queryStr: string) => {
        if (!queryStr) return text

        const safe = queryStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`(${safe})`, 'gi')
        const parts = text.split(regex)

        return (
            <>
                {parts.map((part, index) => {
                    const isMatch = part.toLowerCase() === queryStr.toLowerCase()

                    return (
                        <span key={index} style={{ fontWeight: isMatch ? 700 : 300 }}>
                            {part}
                        </span>
                    )
                })}
            </>
        )
    }

    const isMaxReached = maxItems !== undefined && values.length >= maxItems

    const handleAdd = (itemId: number) => {
        if (isMaxReached) return
        if (!values.includes(itemId)) {
            onChange([...values, itemId])
        }

        setQuery('')
        setOpen(false)
    }

    const handleRemove = (itemId: number) => {
        onChange(values.filter((v) => v !== itemId))
    }

    const selectedObjects = values
        .map((id) => relatives.find((u) => u.id === id))
        .filter(Boolean) as RelativeItem[]

    return (
        <div className='nb-language-autocomplete'>
            <div className='nb-language-wrapper'>
                {selectedObjects.map((item: RelativeItem) => (
                    <div key={item.id} className="autocomplete-options-selected">
                        <span>{item.fullName} {item.category ? `(${item.category})` : ''}</span>
                        <div onClick={() => handleRemove(item.id)}>
                            <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
                        </div>
                    </div>
                ))}

                <input
                    className='tap-add-experience-input-category subscribe-input'
                    placeholder={isMaxReached ? `Limit of ${maxItems} relatives reached` : placeholder}
                    value={query}
                    disabled={isMaxReached}
                    onChange={(e) => {
                        if (!isMaxReached) {
                            setQuery(e.target.value)
                            setOpen(true)
                        }
                    }}
                    onFocus={() => {
                        if (!isMaxReached) setOpen(true)
                    }}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                />
            </div>

            {open && !isMaxReached && (
                <div className='nb-autocomplete-menu-black' onScroll={handleScroll}>
                    {loading && (
                        <div className='nb-autocomplete-menu-item-black'>Loading relatives...</div>
                    )}

                    {!loading && visible.length === 0 && (
                        <div className='nb-autocomplete-menu-item-black'>
                            {relatives.length === 0 ? 'No relatives registered in your profile' : 'No more relatives available'}
                        </div>
                    )}

                    {!loading &&
                        visible.map((item) => (
                            <div
                                key={item.id}
                                className='nb-autocomplete-menu-item-black'
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleAdd(item.id)}
                            >
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span>{highlightMatch(item.fullName, debounced)}</span>
                                    <span className="text-muted" style={{ fontSize: '11px' }}>
                                        {item.relationship ? `${item.relationship} · ` : ''}
                                        {item.category ? item.category.toUpperCase() : ''}
                                        {item.age ? ` (${item.age} yrs)` : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    )
}

